<div class="container">

  <header class="page-heading">
    <h1>Inflation Calculator</h1>
    <p>Calculate the future cost of an item or the future purchasing power of your money, adjusted for inflation.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">Inflation Calculator</div>

    <div class="mode-toggle mb-md">
      <button type="button" class="mode-btn" [class.active]="mode==='futureCost'" (click)="setMode('futureCost')">
        Future Cost of Today's Amount
      </button>
      <button type="button" class="mode-btn" [class.active]="mode==='purchasingPower'" (click)="setMode('purchasingPower')">
        Future Purchasing Power
      </button>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('amount')">
        <label class="field-label" for="amount">
          {{ mode === 'futureCost' ? "Today's Cost" : "Today's Amount" }}
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="amount" type="number"
            [(ngModel)]="amount"
            [min]="C.amount.min" [max]="C.amount.max" [step]="C.amount.step"
            (ngModelChange)="onFieldChange('amount')"
            (blur)="onFieldBlur('amount')"
            placeholder="e.g. 1,00,000"
            autocomplete="off"/>
        </div>
        @if (hasError('amount')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('amount') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('annualRate')">
        <label class="field-label" for="annualRate">Inflation Rate</label>
        <div class="input-shell suffix">
          <input id="annualRate" type="number"
            [(ngModel)]="annualRate"
            [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
            (ngModelChange)="onFieldChange('annualRate')"
            (blur)="onFieldBlur('annualRate')"
            placeholder="e.g. 6"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('annualRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annualRate') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group" [class.has-error]="hasError('years')">
      <label class="field-label" for="years">Duration</label>
      <div class="input-shell suffix">
        <input id="years" type="number"
          [(ngModel)]="years"
          [min]="C.years.min" [max]="C.years.max" [step]="C.years.step"
          (ngModelChange)="onFieldChange('years')"
          (blur)="onFieldBlur('years')"
          placeholder="e.g. 10"
          autocomplete="off"/>
        <span class="affix right">Yr</span>
      </div>
      @if (hasError('years')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('years') }}
        </div>
      }
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      @if (mode === 'futureCost') {
        <div class="summary-card c-blue">
          <div class="sc-icon"></div>
          <div class="sc-label">Future Cost</div>
          <div class="sc-value">{{ fmt.currency(result.result) }}</div>
          <div class="sc-sub">in {{ years }} years</div>
        </div>
        <div class="summary-card c-red">
          <div class="sc-icon"></div>
          <div class="sc-label">Extra You'll Pay</div>
          <div class="sc-value">{{ fmt.currency(result.result - amount) }}</div>
          <div class="sc-sub">@ {{ annualRate }}% inflation</div>
        </div>
      } @else {
        <div class="summary-card c-blue">
          <div class="sc-icon"></div>
          <div class="sc-label">Future Purchasing Power</div>
          <div class="sc-value">{{ fmt.currency(result.result) }}</div>
          <div class="sc-sub">what {{ fmt.currency(amount) }} today will feel like in {{ years }} years</div>
        </div>
        <div class="summary-card c-red">
          <div class="sc-icon"></div>
          <div class="sc-label">Value Eroded</div>
          <div class="sc-value">{{ fmt.currency(amount - result.result) }}</div>
          <div class="sc-sub">@ {{ annualRate }}% inflation</div>
        </div>
      }
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">{{ mode === 'futureCost' ? 'Cost Over Time' : 'Purchasing Power Over Time' }}</div>
      <div class="chart-panel">
        <div class="chart-canvas-wrap">
          <canvas #growthRef></canvas>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Year-wise Breakdown</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Year</th>
              <th class="t-balance">{{ mode === 'futureCost' ? 'Cost' : 'Purchasing Power' }}</th>
            </tr>
          </thead>
          <tbody>
            @for (row of result.years; track row.year) {
              <tr>
                <td class="t-left t-year">Year {{ row.year }}</td>
                <td class="t-balance">{{ fmt.currency(row.value) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How Does Inflation Affect Money?</h2>
    <p>
      Inflation erodes the purchasing power of money over time &mdash; the same amount buys less
      in the future than it does today. This calculator works in two directions: it can show what
      today's cost of an item will be N years from now, or what today's amount will feel worth in
      the future once inflation has eaten into it.
    </p>
    <p class="emi-formula">Future Cost = P &times; (1 + i)<sup>n</sup> &nbsp;|&nbsp; Purchasing Power = P &divide; (1 + i)<sup>n</sup></p>
    <ul class="emi-formula-vars">
      <li><strong>P</strong> &mdash; Today's amount</li>
      <li><strong>i</strong> &mdash; Annual inflation rate</li>
      <li><strong>n</strong> &mdash; Number of years</li>
    </ul>
    <p>
      For example, an item costing &#8377;1,00,000 today will cost about &#8377;1.79 lakh in 10
      years at 6% inflation &mdash; and conversely, &#8377;1,00,000 held as cash today will only
      have the purchasing power of about &#8377;55,839 ten years from now.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>What inflation rate should I use for India?</h3>
      <p>
        India's long-term average retail inflation (CPI) has generally been in the 4&ndash;7% range.
        Use a rate that reflects the specific goal you're planning for &mdash; education and
        healthcare costs, for instance, often rise faster than general inflation.
      </p>
    </div>
    <div class="faq-item">
      <h3>Why does cash lose value even if I don't spend it?</h3>
      <p>
        Because prices rise over time. If your savings don't earn a return at least equal to
        inflation, the real, inflation-adjusted value of that money shrinks every year even though
        the number in your account stays the same.
      </p>
    </div>
  </div>
</section>
